import React, { useEffect, useState } from "react";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CreditCard, CheckCircle2, Clock, ArrowRight, Loader2 } from "lucide-react";
import CartalVerifiedPartnersStrip from "../components/CartalVerifiedPartnersStrip";

export default function FinancingPage() {
  const { t, lang } = useI18n();
  const [offers, setOffers] = useState([]);
  const [amount, setAmount] = useState(150000);
  const [months, setMonths] = useState(60);
  const [lead, setLead] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/financing/offers").then(({ data }) => setOffers(data.items || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/financing/leads", lead);
      setSubmitted(true);
    } catch { /* fail silently — user can retry */ } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="financing-page">
      <div className="overline mb-2">FINANCING MARKETPLACE</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium">{lang === "ar" ? "قارن عروض التمويل" : "Compare financing offers"}</h1>
      <p className="text-[#6b6b6b] mt-2 max-w-2xl">{lang === "ar" ? "اطلب من البنوك وشركات التمويل عروضاً وقارنها في دقيقة." : "Request quotes from banks and finance providers and compare in 60 seconds."}</p>

      {/* Phase 2F — CARTAL verified financing partners (silent when none) */}
      <CartalVerifiedPartnersStrip
        categories={["bank", "finance_company", "financing_platform", "leasing_company", "bnpl_service", "fintech"]}
        ctaAr="جهات التمويل الموثّقة في كرتال"
        ctaEn="Verified financing partners on CARTAL"
      />

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 mt-10">
        <div className="surface rounded-3xl p-8 lg:sticky lg:top-24 lg:self-start">
          <div className="overline mb-2">CALCULATOR</div>
          <h2 className="font-display text-2xl font-medium">{lang === "ar" ? "احسب قسطك الشهري" : "Estimate monthly payment"}</h2>
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "مبلغ التمويل (SAR)" : "Loan amount (SAR)"}</label>
              <input type="number" className="input-dark mt-1" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="loan-amount" />
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "المدة (شهر)" : "Term (months)"}</label>
              <input type="number" className="input-dark mt-1" value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="loan-months" />
            </div>
          </div>
          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="financing-lead-form">
            <div className="overline">REQUEST OFFERS</div>
            <input className="input-dark" placeholder={t("common.name")} required value={lead.name} onChange={(e) => setLead({...lead, name: e.target.value})} />
            <input className="input-dark" placeholder={t("common.phone")} required value={lead.phone} onChange={(e) => setLead({...lead, phone: e.target.value})} />
            <input className="input-dark" type="email" placeholder={t("common.email")} required value={lead.email} onChange={(e) => setLead({...lead, email: e.target.value})} />
            <button className="btn-primary w-full justify-center" disabled={busy || submitted} data-testid="financing-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : submitted ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              {submitted ? (lang === "ar" ? "تم الإرسال" : "Sent") : busy ? "..." : (lang === "ar" ? "اطلب العروض" : "Request offers")}
            </button>
          </form>
        </div>

        <div className="space-y-4">
          {offers.map((o) => {
            const monthly = amount * (o.rate_pct / 100 / 12) / (1 - Math.pow(1 + o.rate_pct / 100 / 12, -months)) || amount / months;
            return (
              <div key={o.id} className="surface rounded-2xl p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center " data-testid={`finance-offer-${o.id}`}>
                <div className="w-12 h-12 rounded-xl bg-[#c9a55c]/15 border border-[#c9a55c]/40 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-[#8a6f2e]" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-lg">{lang === "ar" ? o.provider_ar : o.provider_en}</div>
                  <div className="text-xs text-[#6b6b6b]">{o.type}</div>
                  <div className="text-xs text-[#6b6b6b] mt-1 flex gap-3"><span><Clock className="w-3 h-3 inline" /> {o.approval_time_hours}h</span><span>Max {formatSAR(o.max_amount_sar)} SAR</span></div>
                </div>
                <div className="text-end">
                  <div className="text-xs text-[#6b6b6b]">{lang === "ar" ? "قسط شهري تقريبي" : "Est. monthly"}</div>
                  <div className="font-display text-2xl text-[#8a6f2e]">{formatSAR(Math.round(monthly))} <span className="text-xs text-[#6b6b6b]">SAR</span></div>
                  <div className="text-xs text-[#6b6b6b] mt-1">{o.rate_pct}% APR</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
